/* eslint-disable max-lines */
"use client";
import PropTypes from "prop-types";
import isEmpty from "lodash.isempty";
import Chip from "../../../atoms/chip";
import ImageTile from "../../../atoms/imageTile";
import Typography from "../../../atoms/typography";
import React, { useEffect, useState } from "react";
import CustomImage from "../../../atoms/customImage";
import useAnalytics from "../../../../analytics/useAnalytics";
import { renderDateString } from "../../../../utils/pdp/common";
import DeliveryDateCalendarPopup from "./deliveryDateCalendarPopup";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import DeliveryDateSelectorButton from "./deliveryDateSelectorButton";
import DeliveryDateSelector from "../../../molecules/deliveryDateSelector";
import { formatClassNames, updateCurrency } from "../../../../utils/common";
import { cleverTapCommonEvents } from "../../../../analytics/clevertapConstants";
import { labelStyle, constants } from "../../../../constants/pageConstants/pdpConstants";
import { resetShippingLoader } from "../../../../store/slices/common/shippingDateAndSlotSelection";
import { calenderIsTodayOrTomorrowDate, convertTo12Hour } from "../../../../utils/common/deliveryDate";
import { CHANGE, SELECT_DELIVERY_DATE, SELECT_DELIVERY_DATE_AND_TIME_SLOT } from "../../../../constants/common/common";

/**
 * Renders a section with delivery date and time slot options.
 *
 * This component displays delivery date and time inputs based on the given product and catalog information.
 * It provides the full flow of selecting a delivery date and time slot, including appropriate input fields
 * and any associated UI elements. The UI adjusts based on whether the user is on a mobile device or not.
 *
 * @returns {JSX.Element} The rendered DeliveryDateSelection component.
 */
const DeliveryDateSelection = ({
  isMobile,
  currentItem,
  selectedCurrency,
  setSelectedDate,
  selectedDate,
  setHasTimeSlot,
  hasDateSelector,
  setHasDateSelector,
  msgContent,
  isCutoffTimestamp,
  onDateSelection,
  isNonDeliverableToast,
  setNonDeliverebleToast,
  cleverTapData,
  isOutOfStock,
  multiOrderDateRange,
  hasMultiOrderProductChange,
  popupCleverTapData,
  isCart,
  currentShippingMethod,
  setShowDeliveryDetailsDrawer,
  isCourier,
}) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const [hasShowDeliveryDatePopover, setHasShowDeliveryDatePopover] = useState(false);

  const {
    deliveryDates,
    multiProductDateRange,
    blockedDates,
    pinCode,
    selectedDeliveryDetails,
    loading,
    isCartVisible,
    nextAvailableDateDifference,
  } = useSelector(
    (state) => ({
      deliveryDates: state?.shippingDateAndSlotSelection?.deliveryDates,
      selectedIntLocation: state?.locationLock.selectedIntLocation,
      multiProductDateRange: state?.shippingDateAndSlotSelection?.multiProductDateRange,
      blockedDates: state?.shippingDateAndSlotSelection?.blockedDates,
      pinCode: state?.locationLock?.selectedDomesticLocation?.pincode,
      selectedDeliveryDetails: state?.shippingDateAndSlotSelection?.selectedDeliveryDetails,
      nextAvailableDateDifference: state?.shippingDateAndSlotSelection?.nextAvailableDifference,
      loading: state?.shippingDateAndSlotSelection?.loading,
      isCartVisible: state?.cart?.isCartVisible,
    }),
    shallowEqual,
  );

  /**
   *
   * @param {string} date
   * @returns today or tomorrow on the basis of date
   */
  const getTodayOrTomorrow = (date) => {
    if (calenderIsTodayOrTomorrowDate(date)?.isDateToday) {
      return constants?.TODAY;
    } else if (calenderIsTodayOrTomorrowDate(date)?.isDateTomorrow) {
      return constants?.TOMORROW;
    } else {
      return constants?.LATER;
    }
  };

  const domesticDeliveryDates = deliveryDates?.deliveryDates;
  const isDateToday = calenderIsTodayOrTomorrowDate(domesticDeliveryDates?.[0])?.isDateToday;
  const isDateTomorrow = calenderIsTodayOrTomorrowDate(domesticDeliveryDates?.[0])?.isDateTomorrow;

  /**
   * Handles the click event for delivery details.
   */
  const onHandleDeliveryDetailsClick = () => {
    if (isCourier) {
      setSelectedDate("");
    } else {
      setSelectedDate(selectedDeliveryDetails?.selectedDate || "");
    }
    setHasDateSelector(true);
    setHasTimeSlot(true);
    dispatch(resetShippingLoader());
  };

  useEffect(() => {
    if (!selectedDeliveryDetails?.selectedDate && !isCourier) {
      setHasDateSelector(true);
    } else {
      if (isCartVisible) {
        setHasDateSelector(true);
      } else {
        setHasDateSelector(false);
        setHasTimeSlot(false);
      }
    }
  }, [selectedDeliveryDetails]);

  /**
   * Function to toggle the delivery details pop-up and track the event when it is closed.
   * @function
   *
   * @returns {void} This function does not return any value.
   */
  const onHandlePopOverVisibility = () => {
    const willShow = !hasShowDeliveryDatePopover;
    dispatch(resetShippingLoader());
    setHasShowDeliveryDatePopover(willShow);
    if (willShow) {
      trackEvent(cleverTapCommonEvents.deliveryPopupViewed, popupCleverTapData, ["clevertap"]);
    } else {
      const cleverTapDataObject = {
        ...popupCleverTapData,
        is_date_today: selectedDate ? calenderIsTodayOrTomorrowDate(selectedDate)?.isDateToday : "",
        is_date_tomorrow: selectedDate ? calenderIsTodayOrTomorrowDate(selectedDate)?.isDateTomorrow : "",
        date_of_delivery: selectedDate,
        delivery_shipping: currentShippingMethod?.shippingMethodName,
        delivery_shipping_fee: "",
        delivery_slot: "",
      };
      trackEvent(cleverTapCommonEvents.deliveryPopupClosed, cleverTapDataObject, ["clevertap"]);
    }
  };
  /**
   * Handles the selection of a date in the calendar.
   * After selecting a date, it sets the selected date in state and moves the current component to the delivery method.
   *
   * @function
   * @param {string} date - The selected date from the calendar.
   * @returns {void}
   */
  const onDeliveryDateSelection = (date) => {
    onDateSelection(date);
    setHasShowDeliveryDatePopover(false);
    setHasTimeSlot(true);
  };

  /**
   *
   * @returns the tiles to be replaced
   */
  const getDayChip = (chipDate) => {
    let tileText = getTodayOrTomorrow(chipDate);
    const chipSelectedClasses = "border border-darkOliveGreen bg-fnp-50";

    const dateString = renderDateString(chipDate, currentItem, multiProductDateRange, true);
    const isLaterNotSelected =
      calenderIsTodayOrTomorrowDate(selectedDate)?.isDateToday ||
      calenderIsTodayOrTomorrowDate(selectedDate)?.isDateTomorrow;

    const isSameDate = selectedDate === chipDate;
    const isNotSameDate = tileText === "Later" && !isLaterNotSelected && selectedDate;
    return (
      <>
        <Chip
          content={tileText}
          secondaryText={
            tileText === "Later"
              ? !isLaterNotSelected && renderDateString(selectedDate, currentItem, multiProductDateRange, true)
              : dateString
          }
          size="medium"
          classes={`flex-col w-1/${isDateToday ? 3 : 2} min-h-[58px] px-20 py-8 text-14 font-600 justify-center leading-5 items-center hover:border-darkOliveGreen text-fnp-400 hover:cursor-pointer hover:bg-fnp-50 ${isSameDate || isNotSameDate ? chipSelectedClasses : "border-radioUncheck"}`}
          color="default"
          variant="default"
          secondaryTextClasses="text-12 font-500"
          // eslint-disable-next-line react/jsx-handler-names
          onClick={tileText !== "Later" ? () => onDeliveryDateSelection(chipDate) : () => onHandlePopOverVisibility()}
        />
      </>
    );
  };

  /**
   * Renders the input value of delivery text field
   *
   * @function
   * @returns {void}
   */
  const renderDeliveryInputValue = () => {
    if (!isEmpty(selectedDeliveryDetails?.selectedDate)) {
      const startTime12Hour = convertTo12Hour(selectedDeliveryDetails.timeSlots.timeSlots.startTime);
      const endTime12Hour = convertTo12Hour(selectedDeliveryDetails.timeSlots.timeSlots.endTime);
      const price = selectedDeliveryDetails.timeSlots.timeSlots.shippingPrice;
      const dateString = renderDateString(
        selectedDeliveryDetails?.selectedDate,
        currentItem,
        multiProductDateRange,
        false,
        true,
      );

      return (
        <div className={`flex text-left text-sm font-500 text-black-100 ${isMobile ? "flex-col" : "flex-row gap-4"}`}>
          <span className={formatClassNames(isMobile && "flex-shrink-0")}>
            {`${dateString}, ${startTime12Hour} - ${endTime12Hour} ${isMobile ? "" : " -"}`}
          </span>
          <span className={formatClassNames(isMobile && "overflow-hidden truncate whitespace-nowrap")}>
            {`${selectedDeliveryDetails?.currentShippingMethod?.shippingMethodName} : `}
            {updateCurrency(selectedCurrency, price, true, true)}
          </span>
        </div>
      );
    }
  };

  useEffect(() => {
    if (
      pinCode &&
      !isCartVisible &&
      !isEmpty(nextAvailableDateDifference) &&
      isEmpty(selectedDeliveryDetails) &&
      isEmpty(selectedDate)
    ) {
      trackEvent(cleverTapCommonEvents.productAvailableDate, popupCleverTapData, ["clevertap"]);
    }
  }, [nextAvailableDateDifference]);

  if (pinCode && hasDateSelector && domesticDeliveryDates?.length) {
    return (
      <div className="flex w-full flex-col gap-12">
        <div
          className={`flex items-center justify-start ${isCart ? "gap-16 border-b-1 border-[#F5F5F5] px-5 py-16" : ""}`}
        >
          {isCart && (
            // eslint-disable-next-line react/jsx-handler-names
            <div className="w-20 hover:cursor-pointer" onClick={() => setShowDeliveryDetailsDrawer(false)}>
              <ImageTile imgSrc={"/icons/left-arrow.svg"} imgHeight={20} imgWidth={20} imgAlt={"left-side-arrow"} />
            </div>
          )}
          <Typography variant="p" Tag="p" classes={isCart ? `${labelStyle.replace("pb-12", "")} pb-0` : labelStyle}>
            {SELECT_DELIVERY_DATE}
          </Typography>
        </div>
        {!isCourier && (isDateToday || isDateTomorrow) ? (
          <>
            <div className={`flex items-center justify-between gap-8 ${isCart ? "px-5" : ""}`}>
              {deliveryDates.deliveryDates?.map(
                (item, index) => index <= (isDateToday ? 2 : 1) && <>{getDayChip(item)}</>,
              )}
            </div>
          </>
        ) : (
          <>
            <DeliveryDateSelectorButton
              selectedDate={selectedDate}
              onHandlePopOverVisibility={onHandlePopOverVisibility}
              setSelectedDate={setSelectedDate}
              setHasTimeSlot={setHasTimeSlot}
              isCart={isCart}
              isCourier={isCourier}
            />
          </>
        )}
        <DeliveryDateCalendarPopup
          selectedDate={selectedDate}
          currentItem={currentItem}
          isMobile={isMobile}
          blockedDates={blockedDates?.deliveryBlockDates || ""}
          isDisplayCatalogContent={blockedDates?.displayCatalogContent || false}
          isCutoffTimestamp={isCutoffTimestamp}
          isCalender={false}
          deliveryDatesList={deliveryDates?.deliveryDates}
          msgContent={msgContent}
          limitedCatalogContent={blockedDates?.content}
          pincode={pinCode || ""}
          isCart={isCart}
          cleverTapData={cleverTapData}
          onDateSelection={onDeliveryDateSelection}
          hasShowDeliveryDatePopover={hasShowDeliveryDatePopover}
          onHandlePopOverVisibility={onHandlePopOverVisibility}
          hasNonDeliverableToast={isNonDeliverableToast}
          setNonDeliverebleToast={setNonDeliverebleToast}
          isOutOfStock={isOutOfStock}
          hasMultiOrderProductChange={hasMultiOrderProductChange}
          multiOrderDateRange={multiOrderDateRange}
        />
      </div>
    );
  } else if (selectedDate || selectedDeliveryDetails?.selectedDate) {
    return (
      <div className="flex flex-col gap-12">
        <Typography variant="p" Tag="p" classes={labelStyle}>
          {SELECT_DELIVERY_DATE_AND_TIME_SLOT}
        </Typography>
        <DeliveryDateSelector
          onClickHandler={onHandleDeliveryDetailsClick}
          value={renderDeliveryInputValue()}
          errorStyleType={"animate"}
          startAdornment={
            <CustomImage src={`/icons/calender-icon.svg`} width={24} height={24} alt="email icon" sizes="10vw" />
          }
          endAdornment={
            <Typography variant="p" Tag="p" classes="text-12 font-500 text-fnp-400">
              {CHANGE}
            </Typography>
          }
        />
      </div>
    );
  }
};

DeliveryDateSelection.propTypes = {
  isCart: PropTypes.bool,
  isMobile: PropTypes.bool,
  isCourier: PropTypes.bool,
  msgContent: PropTypes.object,
  isOutOfStock: PropTypes.bool,
  setHasTimeSlot: PropTypes.func,
  selectedDate: PropTypes.string,
  setSelectedDate: PropTypes.func,
  cleverTapData: PropTypes.object,
  hasDateSelector: PropTypes.bool,
  onDateSelection: PropTypes.func,
  isCutoffTimestamp: PropTypes.bool,
  selectedCurrency: PropTypes.object,
  setHasDateSelector: PropTypes.func,
  multiOrderDateRange: PropTypes.array,
  popupCleverTapData: PropTypes.object,
  isNonDeliverableToast: PropTypes.bool,
  currentShippingMethod: PropTypes.object,
  setNonDeliverebleToast: PropTypes.func,
  currentItem: PropTypes.object.isRequired,
  hasMultiOrderProductChange: PropTypes.bool,
  setShowDeliveryDetailsDrawer: PropTypes.func,
};

export default DeliveryDateSelection;
